import React from 'react';
import { InfluencerProfileData } from "types";
import { Bundle, Influencer, InfluencerLocation, InfluencerLocationInput } from "@foodfluence/shared";
import { parsePhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { noSameLocation, locationsHaveChanged } from 'utils/locationUtils';

export const getFormattedBundleDescription = (bundle: Bundle): string => {
  const { deliverableCounts, cash } = bundle;
  const { instagramReels, instagramPosts, instagramStories, tiktoks } = deliverableCounts;

  const parts = [];
  if (instagramReels > 0) parts.push(`${instagramReels} IG Reel${instagramReels > 1 ? 's' : ''}`);
  if (instagramPosts > 0) parts.push(`${instagramPosts} IG Post${instagramPosts > 1 ? 's' : ''}`);
  if (instagramStories > 0) parts.push(`${instagramStories} IG Story Frame${instagramStories > 1 ? 's' : ''}`);
  if (tiktoks > 0) parts.push(`${tiktoks} TikTok${tiktoks > 1 ? 's' : ''}`);

  return `${parts.join(', ')} - $${cash}`;
};

// Helper function to compare rates
const ratesEqual = (formRate: number | undefined, convertedRate: number | undefined) => {
  return (formRate ?? 0) === (convertedRate ?? 0);
};

export const hasFormChanged = (influencerData: Influencer | null, formData: InfluencerProfileData | null, influencerLocations: InfluencerLocation[] | null, formLocations: InfluencerLocationInput[] | null) => {
  const convertedInfluencerData = InfluencerDataToInfluencerProfileData(influencerData, influencerLocations);
  
  if (!convertedInfluencerData || !formData) {
    return true;
  }

  const fullPhoneNumber = `${formData.countryCode || ''}${formData.phoneNumber || ''}`;
  const convertedPhoneNumber = `${convertedInfluencerData.countryCode || ''}${convertedInfluencerData.phoneNumber || ''}`;

  return (
    // Check if locations have changed
    locationsHaveChanged(formLocations, convertedInfluencerData.location) ||
    // Original comparison logic for other fields
    formData.firstName !== convertedInfluencerData.firstName ||
    formData.lastName !== convertedInfluencerData.lastName ||
    formData.email_address !== convertedInfluencerData.email_address ||
    fullPhoneNumber !== convertedPhoneNumber ||
    formData.ethnicity !== convertedInfluencerData.ethnicity ||
    formData.birthday !== convertedInfluencerData.birthday ||
    formData.gender !== convertedInfluencerData.gender ||
    (formData.pronouns || []).join(',') !== (convertedInfluencerData.pronouns || []).join(',') ||
    (formData.languages || []).join(',') !== (convertedInfluencerData.languages || []).join(',') ||
    formData.tiktokHandle !== convertedInfluencerData.tiktokHandle ||
    (formData.associatedTags || []).join(',') !== (convertedInfluencerData.associatedTags || []).join(',') ||
    formData.collabPreference !== convertedInfluencerData.collabPreference ||
    // minRate range comparisons
    formData.rateRanges?.instagramPosts?.offered !== convertedInfluencerData.rateRanges?.instagramPosts?.offered ||
    !ratesEqual(formData.rateRanges?.instagramPosts?.minRate, convertedInfluencerData.rateRanges?.instagramPosts?.minRate) ||
    formData.rateRanges?.instagramReels?.offered !== convertedInfluencerData.rateRanges?.instagramReels?.offered ||
    !ratesEqual(formData.rateRanges?.instagramReels?.minRate, convertedInfluencerData.rateRanges?.instagramReels?.minRate) ||
    formData.rateRanges?.instagramStories?.offered !== convertedInfluencerData.rateRanges?.instagramStories?.offered ||
    !ratesEqual(formData.rateRanges?.instagramStories?.minRate, convertedInfluencerData.rateRanges?.instagramStories?.minRate) ||
    formData.rateRanges?.tiktoks?.offered !== convertedInfluencerData.rateRanges?.tiktoks?.offered ||
    !ratesEqual(formData.rateRanges?.tiktoks?.minRate, convertedInfluencerData.rateRanges?.tiktoks?.minRate) ||
    !ratesEqual(formData.rateRanges?.gifting?.minRate, convertedInfluencerData.rateRanges?.gifting?.minRate) ||
    formData.bundlePricing !== convertedInfluencerData.bundlePricing ||
    formData.mediaKit !== convertedInfluencerData.mediaKit
  );
};

export const areProfileFieldsFilled = (formData: InfluencerProfileData, useCase: 'missingFields' | 'editing') => {
  return (
    formData?.firstName?.trim() !== '' && formData?.firstName?.trim().toLowerCase() !== 'unknown' &&
    formData?.lastName?.trim() !== '' && formData?.lastName?.trim().toLowerCase() !== 'unknown' &&
    formData?.email_address?.trim() !== '' &&
    formData?.phoneNumber?.trim() !== '' &&
    formData?.countryCode?.trim() !== '' &&
    formData?.ethnicity != undefined && formData?.ethnicity?.trim() !== '' &&
    formData?.gender != undefined && formData?.gender?.trim() !== '' &&
    formData?.pronouns?.length > 0 &&
    (useCase === 'missingFields' ? formData?.location?.length > 0 && formData?.location.every(loc => loc.lat !== 0 && loc.lng !== 0) : true) &&
    (useCase === 'editing' ? (formData?.location?.length == 1 && formData?.location[0].lat !== 0 && formData?.location[0].lng !== 0) ||
    (formData?.location?.length > 1 && formData?.location.slice(0, -1).every(loc => loc.lat !== 0 && loc.lng !== 0)) : true) &&
    formData?.associatedTags?.length > 0 &&
    formData?.collabPreference !== null && formData?.collabPreference.trim() !== '' &&
    formData?.rateRanges?.instagramPosts?.offered !== undefined &&
    formData?.rateRanges?.instagramReels?.offered !== undefined &&
    formData?.rateRanges?.instagramStories?.offered !== undefined &&
    formData?.rateRanges?.tiktoks?.offered !== undefined &&
    (formData?.rateRanges?.tiktoks?.offered ? formData?.tiktokHandle?.trim() !== '' : true)
  );
};



export const validateEmail = (newEmail_address: string) => {
  if (!newEmail_address.includes('@')) {
    return false;
  }
  return true;
}

export const isSubmitEnabled = (influencerData: Influencer | null, formData: InfluencerProfileData | null, influencerLocations: InfluencerLocation[] | null, formLocations: InfluencerLocationInput[] | null) => {
  // If formData is null, submit should be disabled
  if (!formData) {
    return false;
  }

  const areProfileFieldsValid = areProfileFieldsFilled(formData, 'editing');
  
  const instagramOrTiktok = 
    ((formData.rateRanges?.instagramPosts?.offered && 
      formData.rateRanges?.instagramReels?.offered && 
      formData.rateRanges?.instagramStories?.offered && 
      formData.instagramHandle?.trim() !== '') ||
    (formData.rateRanges?.tiktoks?.offered && 
      formData.tiktokHandle?.trim() !== '')) ?? false;

  const areGiftingRatesValid = 
    formData.collabPreference === 'cashOnly' || 
    ((formData.rateRanges?.gifting?.minRate ?? 0) > 0);

  const areInstagramRatesValid = 
    !formData.rateRanges?.instagramReels?.offered || 
    ((formData.rateRanges?.instagramReels?.minRate ?? 0) > 0) &&
    ((formData.rateRanges?.instagramPosts?.minRate ?? 0) > 0) &&
    ((formData.rateRanges?.instagramStories?.minRate ?? 0) > 0);

  const areTiktokRatesValid = 
    !formData.rateRanges?.tiktoks?.offered || 
    ((formData.rateRanges?.tiktoks?.minRate ?? 0) > 0);

  const areLocationsValid = 
    (formData.location?.length ?? 0) > 0 && 
    (formData.location ? noSameLocation(formData.location) : false);

  const isEmailValid = validateEmail(formData.email_address ?? '');

  return hasFormChanged(influencerData, formData, influencerLocations, formLocations) && 
    areProfileFieldsValid && 
    areGiftingRatesValid && 
    areInstagramRatesValid && 
    areTiktokRatesValid && 
    instagramOrTiktok && 
    areLocationsValid && 
    isEmailValid;
};

export const cashCap = 10000;

export const handleIncrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
  setter(value + 1);
};

export const handleDecrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
  if (value > 0) {
    setter(value - 1);
  }
}; 

export const InfluencerDataToInfluencerProfileData = (influencerData: Influencer | null, influencerLocations: InfluencerLocation[] | null): InfluencerProfileData => {
  // Return default values if influencerData is null
  if (!influencerData || !influencerLocations) {
    return {
      firstName: '',
      lastName: '',
      email_address: '',
      phoneNumber: '',
      instagramHandle: '',
      tiktokHandle: '',
      countryCode: '',
      gender: '',
      pronouns: [],
      ethnicity: '',
      languages: [],
      birthday: '',
      associatedTags: [],
      collabPreference: '',
      rateRanges: {
        instagramPosts: { offered: false, minRate: 0 },
        instagramReels: { offered: false, minRate: 0 },
        instagramStories: { offered: false, minRate: 0 },
        tiktoks: { offered: false, minRate: 0 },
        gifting: { minRate: 0 }
      },
      bundlePricing: [],
      mediaKit: null,
      location: influencerLocations || []
    };
  }

  const initialPhone = parsePhone(influencerData?.phoneNumber || '');
  const initialPhoneNumber = initialPhone.nationalNumber;
  const initialCountryCode = initialPhone.countryCode;
  return {
    firstName: influencerData.firstName,
    lastName: influencerData.lastName,
    email_address: influencerData.email_address,
    phoneNumber: initialPhoneNumber,
    countryCode: initialCountryCode,
    instagramHandle: influencerData.instagramHandle ?? '',
    tiktokHandle: influencerData.tiktokHandle ?? '',
    gender: influencerData.gender,
    pronouns: influencerData.pronouns,
    ethnicity: influencerData.ethnicity,
    languages: influencerData.languages || [],
    birthday: influencerData?.birthday || '',
    associatedTags: influencerData.associatedTags,
    collabPreference: influencerData.collabPreference,
    rateRanges: influencerData.rateRanges,
    bundlePricing: influencerData?.bundlePricing,
    mediaKit: influencerData?.mediaKit,
    location: influencerLocations,
  };
};

export const parsePhone = (fullNumber: string) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(fullNumber);

    if (!phoneNumber) {
      return {
        countryCode: '+1',
        nationalNumber: fullNumber.replace(/[^\d]/g, ''),
        country: 'US'
      };
    }
    return {
      countryCode: `+${phoneNumber.countryCallingCode}`,
      nationalNumber: phoneNumber.nationalNumber,
      country: phoneNumber.country
    };
  } catch (error) {
    return {
      countryCode: '+1',
      nationalNumber: fullNumber.replace(/[^\d]/g, ''),
      country: 'US'
    };
  }
};

